import { useEffect, useState } from "react"

import { logger } from "@/lib/logger"
import { peaksResponseSchema } from "@/utils/validation/externalBoundarySchemas"

const log = logger("usePrecomputedPeaks")
// Typical signed peaks reads finish in hundreds of milliseconds; decoding must remain available during an outage.
export const PEAKS_LOAD_TIMEOUT_MS = 2000
export type WavesurferPeaks = Array<Float32Array | number[]>

interface PeaksResult {
  url: string
  peaks: WavesurferPeaks | null
  durationSeconds: number | null
}

/** Read legacy mono or v2 recorded-channel peaks, falling back to decoding on failure. */
export function usePrecomputedPeaks(peaksUrl: string | null | undefined): {
  peaks: WavesurferPeaks | null
  durationSeconds: number | null
  isLoading: boolean
} {
  const [result, setResult] = useState<PeaksResult | null>(null)
  useEffect(() => {
    if (!peaksUrl) {
      return
    }
    const controller = new AbortController()
    let cancelled = false
    const timeout = setTimeout(() => controller.abort(), PEAKS_LOAD_TIMEOUT_MS)
    void fetch(peaksUrl, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Peaks fetch failed: ${response.status}`)
        }
        const parsed = peaksResponseSchema.safeParse(await response.json())
        if (!parsed.success) {
          throw new Error("Invalid waveform peaks artifact")
        }
        const data = parsed.data
        const channels = Array.isArray(data) ? [data] : data.channels
        const peaks = channels.map((channel) =>
          Float32Array.from(channel.flat()),
        )
        if (!cancelled) {
          setResult({
            url: peaksUrl,
            peaks,
            durationSeconds: Array.isArray(data) ? null : data.duration_seconds,
          })
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          log.warn(
            "Failed to load precomputed peaks, falling back to client-side computation",
            { error },
          )
          setResult({ url: peaksUrl, peaks: null, durationSeconds: null })
        }
      })
      .finally(() => clearTimeout(timeout))
    return () => {
      cancelled = true
      clearTimeout(timeout)
      controller.abort()
    }
  }, [peaksUrl])

  return {
    peaks: peaksUrl && result?.url === peaksUrl ? result.peaks : null,
    durationSeconds:
      peaksUrl && result?.url === peaksUrl ? result.durationSeconds : null,
    isLoading: !!peaksUrl && result?.url !== peaksUrl,
  }
}
